import {
  ScaffolderInputHeader,
  ScaffolderFormPaper,
  ScaffolderFormHeader,
  ScaffolderFieldError,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useEffect, useRef } from 'react';

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
} from '@material-ui/core';

import { NOTIFICATIONS_INFO, SLACK_CHANNEL_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import { NotificationService, ProjectNotificationValue } from '../types';
import { notificationInputErrors } from '../validation';

interface NotificationSectionProps {
  value: ProjectNotificationValue;
  onChange: (value: ProjectNotificationValue) => void;
  errors?: string[];
}

const NotificationSectionInner = ({
  value,
  onChange,
  errors = [],
}: NotificationSectionProps) => {
  const classes = useStyles();

  const channelError =
    errors.length > 0
      ? notificationInputErrors(
          value.notificationChannel,
          value.notificationServices,
        )
      : '';
  const hasChannelError = channelError !== '';

  const channelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasChannelError) {
      channelRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [hasChannelError]);

  const handleServiceToggle = (service: NotificationService) => {
    const current = value.notificationServices || [];
    const updated = current.includes(service)
      ? current.filter(s => s !== service)
      : [...current, service];
    onChange({ ...value, notificationServices: updated });
  };

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="Notifications"
          description="Configure notification settings"
          divider
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Notification services"
            description="Select the services that should receive notifications for this project."
            infoDoc={NOTIFICATIONS_INFO}
          />
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={(value.notificationServices || []).includes('slack')}
                  onChange={() => handleServiceToggle('slack')}
                />
              }
              label="Slack"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={(value.notificationServices || []).includes(
                    'grafana',
                  )}
                  onChange={() => handleServiceToggle('grafana')}
                />
              }
              label="Grafana"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={(value.notificationServices || []).includes(
                    'github',
                  )}
                  onChange={() => handleServiceToggle('github')}
                />
              }
              label="GitHub"
            />
          </FormGroup>
        </Box>
        <div ref={channelRef} className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Slack notifications channel"
            description="Configure Slack channel where notifications will be sent for this project"
            infoDoc={SLACK_CHANNEL_INFO}
          />
          <TextField
            id="notification-slack-channel"
            label="Slack Channel"
            value={value.notificationChannel || ''}
            onChange={e =>
              onChange({ ...value, notificationChannel: e.target.value })
            }
            variant="outlined"
            color="secondary"
            size="small"
            fullWidth
            required={(value.notificationServices || []).includes('slack')}
            error={hasChannelError}
            inputProps={{ maxLength: 63 }}
            disabled={!(value.notificationServices || []).includes('slack')}
            helperText={
              hasChannelError ? (
                <ScaffolderFieldError message={channelError} />
              ) : (
                'Up to 63 characters. Allowed: lowercase letters, numbers, hyphens, underscores.'
              )
            }
          />
        </div>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const NotificationSection = React.memo(NotificationSectionInner);
